import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';

import { tableNames, propertyNames } from '../../app/app.constants';
import { GlobalVars, dynamicSort } from '../../app/app.environment';
import { Check } from '../../models/check';
import { QueryModel } from '../../models/query';
import { Timetable } from '../../models/timetable';
import { User } from '../../models/user';
import { UserCreds } from '../../models/user-creds';
import { AuthProvider } from '../auth/auth';
import { FirebaseProvider } from '../firebase/firebase';

@Injectable()
export class UserProvider {

  constructor(
    public authProvider: AuthProvider,
    public firebaseService: FirebaseProvider,
    public globalVars: GlobalVars,
  ) {}
  
  /**
   * Recuperar checks de l'usuari actual.
   */
  getChecks(): Observable<Check[]> {
    return this.firebaseService.object(this.getBaseRoot() + this.globalVars.user.key + '/' + propertyNames.Checks);
  }

  /**
   * Recuperar últim horari de l'usuari indicat o l'usuari actual (per defecte).
   */
  getLastTimetable(userId?): Observable<any> {
    return this.firebaseService.property(this.getBaseRoot() + (userId || this.globalVars.user.key) + '/' + propertyNames.LastTimetable);
  }

    /**
   * Recuperar últim horari de l'usuari actual.
   */
  getLastTimetableOnce(): Observable<any> {
    return this.firebaseService.propertyOnce(this.getBaseRoot() + this.globalVars.user.key + '/' + propertyNames.LastTimetable);
  }

  /**
   * Recuperar nom d'un usuari segons ID.
   * 
   * @param userId ID de l'usuari.
   */
  getNameOnce(userId: string): Observable<any> {
    return this.firebaseService.propertyOnce(this.getBaseRoot() + userId + '/' + propertyNames.Name)
  }

  /**
   * Recuperar estat de l'usuari indicat o l'usuari actual (per defecte).
   */
  getState(userId?): Observable<any> {
    return this.firebaseService.property(this.getBaseRoot() + (userId || this.globalVars.user.key) + '/' + propertyNames.State);
  }

  /**
   * Recuperar horaris de l'usuari indicat o l'usuari actual (per defecte).
   */
  getTimetables(userId?): Observable<Timetable[]> {
    return this.firebaseService.list(this.getBaseRoot() + (userId || this.globalVars.user.key) + '/' + tableNames.Timetables);
  }

  /**
   * Recuperar informació de l'usuari segons ID.
   * 
   * @param userId ID de l'usuari. Per defecte es fa servir la ID de l'usuari actual.
   */
  getUser(userId?: string): Observable<User> {
    return this.firebaseService.object(this.getBaseRoot() + (userId || this.globalVars.user.key));
  }

  /**
   * Recuperar usuaris.
   */
  getUsers(): Observable<User[]> {
    return this.firebaseService.list(this.getBaseRoot()).map(users => users.sort(dynamicSort('name')));
  }

  /**
   * Recuperar usuaris segons un criteri de cerca.
   * 
   * @param criteria Criteri de cerca.
   * @param field Camp utilitzat per filtrar.
   */
  getUsersBySearch(criteria?: string, field?: string): Observable<User[]> {
    return this.getUsers().map(users => users.filter(user => {
      return (user[field].toLowerCase().indexOf(criteria.toLowerCase()) > -1);
    }))
  }

  /**
   * Recuperar noms dels usuaris del repositori indicat.
   * 
   * @param repo Repositori. Per defecte es fa servir el repositori de l'usuari actual.
   */
  getUserNames(repo?: string): Observable<any[]> {
    let query = new QueryModel();

    query.orderByChild = 'repo';
    query.equalTo = repo ? repo : this.globalVars.user.repo;

    return this.firebaseService.list(tableNames.User, query)
      .map(users => users.map(user => { return {key: user.key, name: user.name} }).sort(dynamicSort('name')));
  }

  /**
   * Recuperar noms dels usuaris del repositori indicat segons un criteri de cerca.
   * 
   * @param repo Repositori. Per defecte es fa servir el repositori de l'usuari actual.
   * @param criteria Criteri de cerca.
   * @param field Camp utilitzat per filtrar.
  */
  getUserNamesBySearch(criteria?: string, field?: string): Observable<any[]> {
    return this.getUserNames().map(users => users.filter(user => {
      return (user[field].toLowerCase().indexOf(criteria.toLowerCase()) > -1);
    }))
  }

  /**
   * Recuperar dades públiques dels usuaris del repositori indicat.
   * 
   * @param repo Repositori. Per defecte es fa servir el repositori de l'usuari actual.
   */
  getUsersPublicDataOnce(repo?: string): Observable<User[]> {
    let query = new QueryModel();

    query.orderByChild = 'repo';
    query.equalTo = repo ? repo : this.globalVars.user.repo;

    return this.firebaseService.listOnce(tableNames.User, query);
  }

  getUserWorkplace(userId?: string) {
    return this.firebaseService.property(this.getBaseRoot() + (userId || this.globalVars.user.key) + '/' + propertyNames.Workplace).map(workplace => workplace.value);
  }

  /**
   * Guardar horari a l'usuari actual.
   * 
   * @param timetable Horari a guardar.
   */
  pushTimetable(timetable: any): any {
    return this.firebaseService.push(this.getBaseRoot() + this.globalVars.user.key + '/' + tableNames.Timetables, timetable);
  }

  /**
   * Registrar un usuari al sistema.
   * 
   * @param user Usuari a registrar.
   */
  register(user: User): Promise<any> {    
    return new Promise((resolve, reject) => {
      this.authProvider.signUp({ email: user.email, password: user.password } as UserCreds).then(userUid => {

        delete user.password;
        user.role = Number(user.role);
        user.repo = this.globalVars.user.repo;

        // Preparar dades públiques de l'usuari
        let userPublicData = Object.assign({}, user);
        delete userPublicData.state;
        delete userPublicData.timetables;

        // Preparar inserció simultània
        let mainPath = tableNames.User
        
        var updates = {};
        updates[mainPath + '/' + userUid] = userPublicData;
        // updates[this.getBaseRoot() + '/' + userUid] = user;
        
        this.firebaseService.directUpdate(updates).then(() => {
          resolve(true)
        }).catch(() => {
          reject('Error al registrar usuario.')
        })
      }).catch(err => {
        console.log(err)
        reject('Error al registrar usuario.')
      })
    })
  }

  /**
   * Guardar usuari al sistema.
   * 
   * @param user Usuari a guardar.
   */
  save(user: User): Promise<any> {
    if(user.key) {
      var key = user.key;
      delete user.key;
      return this.firebaseService.set(this.getBaseRoot(), key, user)
    }
      
    else return this.register(user);
  }

  /**
   * Arrel base dins la base de dades.
   */
  private getBaseRoot(): string {
    return tableNames.Repo + '/' + this.globalVars.user.repo + '/' + tableNames.User + '/';
  }
}



// WEBPACK FOOTER //
// ./src/providers/user/user.ts